import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/feature/SEO';
import { siteInfo } from '@/mocks/site';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';

const faqItems = [
  { qKey: 'faq_q1', aKey: 'faq_a1' },
  { qKey: 'faq_q2', aKey: 'faq_a2' },
  { qKey: 'faq_q3', aKey: 'faq_a3' },
  { qKey: 'faq_q4', aKey: 'faq_a4' },
];

const whyItems = [
  { icon: 'ri-user-star-line', titleKey: 'why_card_1_title', descKey: 'why_card_1_desc' },
  { icon: 'ri-stack-line', titleKey: 'why_card_2_title', descKey: 'why_card_2_desc' },
  { icon: 'ri-file-search-line', titleKey: 'why_card_3_title', descKey: 'why_card_3_desc' },
  { icon: 'ri-bar-chart-box-line', titleKey: 'why_card_4_title', descKey: 'why_card_4_desc' },
];

export default function Home() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, []);

  const featuredArticles = articles.slice(0, 6);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteInfo.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '海淀区',
      streetAddress: '中关村南大街5号伦理大厦12层',
      addressCountry: 'CN',
    },
    foundingDate: '2018',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: t(item.qKey),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(item.aKey),
      },
    })),
  };

  return (
    <>
      <SEO
        title={siteInfo.name}
        description={siteInfo.description}
        keywords="伦理在线,伦理学课程,职业道德教育,伦理案例分析,道德决策培训,伦理知识库,在线伦理测评"
        canonicalUrl={siteInfo.url}
        ogType="website"
        schema={{ ...homeSchema, ...orgSchema, ...faqSchema }}
      />

      <main>
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20scholarly%20library%20with%20soft%20natural%20light%20streaming%20through%20tall%20windows%2C%20wooden%20bookshelves%20filled%20with%20classic%20books%2C%20reading%20table%20with%20open%20philosophy%20book%2C%20warm%20golden%20amber%20and%20cream%20tones%2C%20academic%20atmosphere%2C%20serene%20and%20inspiring%2C%20soft%20depth%20of%20field%2C%20artistic%20composition&width=1800&height=900&seq=hero-bg-main&orientation=landscape"
              alt="伦理在线"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-28 text-center">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight max-w-4xl mx-auto">
              {t('hero_title')}
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('hero_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/ethical-theory"
                className="px-8 py-3.5 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('hero_cta_explore')}
              </Link>
              <Link
                to="/category/ethics-assessment"
                className="px-8 py-3.5 rounded-md bg-white/15 backdrop-blur-sm text-white border border-white/25 font-medium text-sm hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('hero_cta_assessment')}
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center p-5 md:p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-article-line text-xl"></i>
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {siteInfo.stats.articles.toLocaleString()}+
                </div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{t('stats_articles')}</div>
              </div>
              <div className="text-center p-5 md:p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-user-line text-xl"></i>
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {siteInfo.stats.users.toLocaleString()}+
                </div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{t('stats_users')}</div>
              </div>
              <div className="text-center p-5 md:p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-team-line text-xl"></i>
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {siteInfo.stats.experts}
                </div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{t('stats_experts')}</div>
              </div>
              <div className="text-center p-5 md:p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-book-open-line text-xl"></i>
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {siteInfo.stats.courses}
                </div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{t('stats_courses')}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {t('section_categories')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                {t('section_categories_desc')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer text-center"
                >
                  <div className={`w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full ${
                    cat.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                    cat.color === 'accent' ? 'bg-accent-100 text-accent-600' :
                    'bg-secondary-100 text-secondary-700'
                  }`}>
                    <i className={`${cat.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed line-clamp-3">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {t('section_featured')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                {t('section_featured_desc')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  slug={article.slug}
                  title={article.title}
                  summary={article.summary}
                  category={article.category}
                  author={article.author}
                  date={article.date}
                  readTime={article.readTime}
                  coverImage={article.coverImage}
                />
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/category/ethical-theory"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors duration-200 cursor-pointer"
              >
                {t('view_all')}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {t('section_why_us')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                {t('section_why_us_desc')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {whyItems.map((item, index) => (
                <div key={index} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                    {t(item.titleKey)}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t(item.descKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {t('section_faq')}
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                {t('section_faq_desc')}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {faqItems.map((item, index) => (
                <details
                  key={index}
                  className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-900 list-none">
                    {t(item.qKey)}
                    <i className="ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-200 group-open:rotate-180"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {t(item.aKey)}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20with%20soft%20amber%20and%20cream%20tones%2C%20subtle%20geometric%20patterns%2C%20scholarly%20atmosphere%2C%20clean%20minimal%20texture%2C%20peaceful%20academic%20backdrop%2C%20light%20airy%20composition&width=1800&height=600&seq=cta-bg-home&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-background-100/80"></div>
          </div>

          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              {t('section_cta_title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-8">
              {t('section_cta_desc')}
            </p>
            <Link
              to="/category/ethical-theory"
              className="inline-flex px-8 py-3.5 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('section_cta_button')}
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}